import { useState, useEffect, useCallback } from 'react';

const useSessionTimeout = (timeoutMinutes = 30, onTimeout, onWarning) => {
  const [isActive, setIsActive] = useState(false); // Start inactive
  const [timeLeft, setTimeLeft] = useState(0); // Start with 0, will be set when active
  const [showWarning, setShowWarning] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has interacted

  // Warning threshold (5 minutes before timeout)
  const warningThreshold = 5 * 60; // 5 minutes in seconds

  // Activity tracking function
  const resetTimer = useCallback(() => {
    if (timeoutMinutes > 0) {
      setIsActive(true);
      setTimeLeft(timeoutMinutes * 60);
      setShowWarning(false);
      setWarningShown(false);
      setIsInitialized(true);
      setHasUserInteracted(true); // Mark that user has interacted
    }
  }, [timeoutMinutes]);

  // Initialize timer when timeout minutes change (e.g., when user logs in)
  useEffect(() => {
    if (timeoutMinutes > 0 && !isInitialized) {
      // Add a small delay to ensure authentication is complete
      const initTimer = setTimeout(() => {
        resetTimer();
      }, 2000); // 2 second delay after authentication
      
      return () => clearTimeout(initTimer);
    }
  }, [timeoutMinutes, isInitialized, resetTimer]);

  // Handle user activity
  useEffect(() => {
    if (timeoutMinutes <= 0) return; // Don't track activity if timeout is disabled

    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    const handleActivity = () => {
      if (isActive) {
        setHasUserInteracted(true); // Mark that user has interacted
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isActive, resetTimer, timeoutMinutes]);

  // Timer countdown
  useEffect(() => {
    if (timeoutMinutes <= 0) return; // Don't run timer if timeout is disabled

    let interval = null;

    // Only start counting down if user has interacted with the page
    if (isActive && timeLeft > 0 && hasUserInteracted) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => {
          const newTimeLeft = timeLeft - 1;
          
          // Show warning when approaching timeout
          if (newTimeLeft <= warningThreshold && !warningShown) {
            setShowWarning(true);
            setWarningShown(true);
            
            // Call warning callback if provided
            if (onWarning) {
              onWarning(Math.ceil(newTimeLeft / 60));
            }
          }
          
          return newTimeLeft;
        });
      }, 1000);
    } else if (timeLeft === 0 && hasUserInteracted) {
      // Session expired - sign out user (only if user has interacted)
      setIsActive(false);
      setShowWarning(false);
      
      // Call timeout callback if provided
      if (onTimeout) {
        onTimeout();
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isActive, timeLeft, warningThreshold, warningShown, onTimeout, onWarning, timeoutMinutes, hasUserInteracted]);

  // Manual session extension
  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Manual session start (for when user logs in)
  const startSession = useCallback(() => {
    if (timeoutMinutes > 0) {
      setIsActive(true);
      setTimeLeft(timeoutMinutes * 60);
      setShowWarning(false);
      setWarningShown(false);
      setIsInitialized(true);
      setHasUserInteracted(false); // Reset interaction flag
    }
  }, [timeoutMinutes]);

  // Format time remaining
  const formatTimeRemaining = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get time remaining in minutes
  const getTimeRemainingMinutes = () => {
    return Math.ceil(timeLeft / 60);
  };

  return {
    isActive,
    timeLeft,
    showWarning,
    extendSession,
    startSession,
    formatTimeRemaining,
    getTimeRemainingMinutes,
    resetTimer
  };
};

export default useSessionTimeout;
